import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import HistoryKpiCards from './HistoryKpiCards';
import {
  clientLabel,
  formatBerlin,
  renderEtkp,
  renderEur,
  renderInt,
  renderRate,
  statusChipColor,
} from './shared';

export interface PlanRow {
  send_type: 'main' | 'dup';
  sam_id: number;
  product_id: number;
  customer_id: string | null;
  score: number | null;
  sam_subject: string | null;
  product_name: string | null;
}

export interface PlanCampaignRow {
  id: number;
  client_code: string;
  send_date: string;
  status: string;
  send_kind: 'main' | 'dup' | 'aa' | 'shadow' | 'reactivation';
  dup_send: number;
  inactive_send: number;
  shadow_of_id: number | null;
  stats_excluded: number;
  stats_excluded_reason: string | null;
  mc_campaign_id: string | null;
  mc_campaign_title: string | null;
  sam_id: number | null;
  sam_subject: string | null;
  product_id: number | null;
  product_name: string | null;
  customer_id: string | null;
  asset_name: string | null;
  asset_isin: string | null;
  recipients: number | null;
  /** Blast row of a day with a dynamic campaign: both audiences, because leads and revenue cover both. */
  attribution_recipients?: number | null;
  // Attribution lands 24-48h+ after the send — NULL = "no data yet", not 0
  // (mysql2 may serialize DECIMAL/BIGINT as strings, hence the unions).
  unique_opens: number | string | null;
  unique_clicks: number | string | null;
  unsubscribed: number | string | null;
  sois: number | string | null;
  leads: number | string | null;
  revenue: number | string | null;
}

export interface PlanRosterRow {
  client_code: string;
  client_name: string;
  shadow_only: number;
}

export interface PlanResponse {
  date: string;
  plan: PlanRow[];
  roster: PlanRosterRow[];
  campaigns: PlanCampaignRow[];
  // Why the KME shadow has no row for this date (skip-chain re-evaluation);
  // null when a KME row exists or no guard matched. Optional: older API.
  kme_shadow_skip_reason?: string | null;
  // Month-phase (2026-09-07): policy label the KME decision was/will be made
  // under (w1..w4, month_end, disabled) and the effective score floor — from
  // the run log for logged dates, replayed otherwise. Optional: older API.
  kme_cadence_phase?: string | null;
  kme_floor_used?: number | null;
  // KME own pick (2026-09-25): the SAM the run chose (or would choose) and
  // where it came from — 'unified' = clone of the day's network pick,
  // 'own' = KME's own best eligible SAM. Optional: older API.
  kme_pick_sam_id?: number | null;
  kme_pick_score?: number | null;
  kme_pick_source?: 'unified' | 'own' | string | null;
  // DNV weekly-cadence wait state (2026-08-05); null when DNV has a main row
  // for the date or cadence is not configured. Optional: older API.
  dnv_cadence?: {
    interval_days: number;
    fallback_after_days: number | null;
    min_score: number | null;
    last_main_date: string | null;
    days_since: number | null;
    pick_score: number | null;
    next_eligible_date: string | null;
    // Month-phase (2026-09-07): effective phase + blackout flag + skip reason.
    phase?: string | null;
    blackout?: boolean;
    reason?: 'main_cadence_not_due' | 'main_month_end_blackout' | null;
  } | null;
}

// Month-phase labels (helpers/adserver/monthPhaseCadence.ts): calendar days,
// w1 = 1-7, w2 = 8-14, w3 = 15-21, w4 = 22-25, month_end = 26-31.
const PHASE_LABELS: Record<string, string> = {
  w1: 'Woche 1 (Tag 1-7)',
  w2: 'Woche 2 (Tag 8-14)',
  w3: 'Woche 3 (Tag 15-21)',
  w4: 'Woche 4 (Tag 22-25)',
  month_end: 'Monatsende (ab Tag 26)',
  disabled: 'Monatsphasen aus',
};
const phaseLabel = (phase: string | null | undefined): string | null =>
  phase ? (PHASE_LABELS[phase] ?? phase) : null;

// Operator-facing labels for the KME-shadow skip reasons (SkipReason union subset
// the shadow orchestrator can return + no_dnv_sam_today from the /plan endpoint).
// Phase-dependent parts (weekday set, floor) come from the API, not from here:
// in w1/w2 Tuesday is open and the floor is 40, otherwise Tue+Fri closed / 45.
const KME_SKIP_LABELS: Record<string, string> = {
  kme_self_send_day: 'eigener KME-Newsletter-Tag (Di/Fr; in Woche 1-2 nur Fr)',
  webinar_heatup_planned: 'Webinar/Heat-Up-Fenster (heute/morgen)',
  plx_mailing_planned: 'PLX-Direktmailing heute geplant',
  sam_send_pause: 'Versandpause',
  pick_score_below_kme_floor: 'Pick-Score unter KME-Schwelle',
  no_dnv_sam_today: 'kein DNV-SAM heute',
  no_unified_pick_today: 'kein Unified-Pick für den Tag',
  kess_weekly_cap_reached: 'Wochen-Cap erreicht (max. 4 Sends/7 Tage)',
  no_kme_candidate: 'kein Kandidat (kein Unified-Pick, eigener KME-Pool leer)',
};

const kmeReasonLabel = (data: PlanResponse): string | null => {
  if (!data.kme_shadow_skip_reason) return null;
  const base = KME_SKIP_LABELS[data.kme_shadow_skip_reason] ?? data.kme_shadow_skip_reason;
  const floor =
    data.kme_shadow_skip_reason === 'pick_score_below_kme_floor' &&
    data.kme_floor_used !== null &&
    data.kme_floor_used !== undefined
      ? ` (<${data.kme_floor_used})`
      : '';
  const phase = phaseLabel(data.kme_cadence_phase);
  return `${base}${floor}${phase ? ` · ${phase}` : ''}`;
};

// DNV weekly-cadence wait state → one operator-readable line.
const dnvCadenceLabel = (c: NonNullable<PlanResponse['dnv_cadence']>): string => {
  const parts: string[] = [];
  if (c.blackout || c.reason === 'main_month_end_blackout') {
    const last =
      c.last_main_date && c.days_since !== null
        ? `letzter Main vor ${c.days_since}d (${c.last_main_date})`
        : 'noch kein Main-Send';
    return `Monatsende-Sperre — kein neuer Main ab Tag 26 (ROI30-Stichtag) · ${last}`;
  }
  const phase = phaseLabel(c.phase);
  if (phase) parts.push(phase);
  if (c.last_main_date && c.days_since !== null) {
    parts.push(`letzter Main vor ${c.days_since}d (${c.last_main_date})`);
  } else {
    parts.push('noch kein Main-Send');
  }
  parts.push(
    `Score ${c.pick_score ?? '–'}${c.min_score !== null ? ` / Floor ${c.min_score}` : ''}`,
  );
  if (c.next_eligible_date) parts.push(`wieder ab ${c.next_eligible_date}`);
  if (c.fallback_after_days !== null) parts.push(`spätestens Tag ${c.fallback_after_days}`);
  return `Kadenz (Intervall ${c.interval_days}d) — ${parts.join(' · ')}`;
};

interface Props {
  data: PlanResponse | null;
  loading: boolean;
  onPreview: (id: number) => void;
}

const toNum = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

const kindChip = (kind: PlanCampaignRow['send_kind']) => {
  switch (kind) {
    case 'shadow':
      return <Chip size="small" label="Shadow" color="info" />;
    case 'aa':
      return <Chip size="small" label="AA" color="info" variant="outlined" />;
    case 'dup':
      return <Chip size="small" label="Dup" />;
    case 'reactivation':
      return <Chip size="small" label="Reakt." variant="outlined" />;
    default:
      return <Chip size="small" label="Main" color="primary" variant="outlined" />;
  }
};

const UnifiedPlanBlock: React.FC<Props> = ({ data, loading, onPreview }) => {
  if (loading && !data) return <CircularProgress sx={{ mb: 2 }} />;
  if (!data) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Unified Tagesplan
      </Typography>

      <Box display="flex" gap={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
        {data.plan.length === 0 && (
          <Card sx={{ backgroundColor: '#ffffff', flex: '1 1 0' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="body2" color="text.secondary">
                Kein Unified-Plan für {data.date} — dunkler Tag (z.B. Webinar-Stand-Down) oder Datum
                vor der Unified-Ära (2026-07-16).
              </Typography>
            </CardContent>
          </Card>
        )}
        {data.plan.map((p) => (
          <Card key={p.send_type} sx={{ backgroundColor: '#ffffff', flex: '1 1 0', minWidth: 260 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary">
                  {p.send_type === 'main' ? 'Main-Pick' : 'Dup-Pick'}
                </Typography>
                <Chip
                  size="small"
                  label={p.score != null ? `Score ${p.score}` : 'Cold-Start'}
                  color={
                    p.score == null
                      ? 'default'
                      : p.score >= 60
                        ? 'success'
                        : p.score >= 40
                          ? 'warning'
                          : 'error'
                  }
                />
              </Box>
              <Typography variant="subtitle2">
                SAM #{p.sam_id}
                {p.sam_subject ? ` — ${p.sam_subject}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Produkt #{p.product_id}
                {p.product_name ? ` — ${p.product_name}` : ''} · Kunde {p.customer_id ?? '–'}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <HistoryKpiCards rows={data.campaigns} scopeLabel={data.date} />

      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                'Client',
                'Art',
                'Status',
                'Kampagne',
                'Empf.',
                'Opens',
                'Clicks',
                'SOI',
                'Leads',
                'Umsatz',
                'ETKP',
                'Sendezeit',
                'Aktion',
              ].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    color: 'black',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.roster.map((client) => {
              const rows = data.campaigns.filter((c) => c.client_code === client.client_code);
              if (rows.length === 0) {
                const kmeReason =
                  client.client_code === 'kme' && data.kme_shadow_skip_reason
                    ? kmeReasonLabel(data)
                    : null;
                const dnvReason =
                  client.client_code === 'dnv' && data.dnv_cadence
                    ? dnvCadenceLabel(data.dnv_cadence)
                    : null;
                return (
                  <TableRow key={client.client_code}>
                    <TableCell>{clientLabel(client.client_code)}</TableCell>
                    <TableCell colSpan={12} sx={{ color: '#999' }}>
                      {kmeReason ? `Shadow entfällt — ${kmeReason}` : (dnvReason ?? 'kein Versand')}
                    </TableCell>
                  </TableRow>
                );
              }
              return rows.map((row, idx) => (
                <TableRow key={row.id} hover>
                  <TableCell>{idx === 0 ? clientLabel(client.client_code) : ''}</TableCell>
                  <TableCell>{kindChip(row.send_kind)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status} color={statusChipColor(row.status)} />
                    {Number(row.stats_excluded) === 1 ? (
                      <Tooltip
                        title={`Aus Statistiken ausgeschlossen: ${row.stats_excluded_reason ?? 'Incident'}`}
                      >
                        <Chip size="small" label="Ausfall" color="warning" sx={{ ml: 0.5 }} />
                      </Tooltip>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ minWidth: 220 }}>
                    {row.send_kind === 'aa' && row.asset_isin ? (
                      <>
                        <code>{row.asset_isin}</code>
                        {row.asset_name ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {row.asset_name}
                          </Typography>
                        ) : null}
                      </>
                    ) : (
                      <>
                        #{row.sam_id ?? '–'}
                        {client.client_code === 'kme' && data.kme_pick_source === 'own' ? (
                          <Tooltip
                            title={`Eigener KME-Pick (Unified-Pick unter der Schwelle) — Score ${data.kme_pick_score ?? '?'}`}
                          >
                            <Chip size="small" label="eigener Pick" color="info" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        ) : null}
                        {row.sam_subject ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {row.sam_subject}
                          </Typography>
                        ) : null}
                      </>
                    )}
                  </TableCell>
                  <TableCell align="right">{renderInt(row.recipients)}</TableCell>
                  <TableCell align="right">
                    {renderInt(toNum(row.unique_opens))}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {renderRate(toNum(row.unique_opens), row.recipients)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {renderInt(toNum(row.unique_clicks))}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {renderRate(toNum(row.unique_clicks), row.recipients)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{renderInt(toNum(row.sois))}</TableCell>
                  <TableCell align="right">
                    {renderInt(toNum(row.leads))}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {renderRate(toNum(row.leads), row.attribution_recipients ?? row.recipients, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{renderEur(row.revenue)}</TableCell>
                  <TableCell align="right">{renderEtkp(row.revenue, row.attribution_recipients ?? row.recipients)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatBerlin(row.send_date)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => onPreview(row.id)}>
                      Vorschau
                    </Button>
                  </TableCell>
                </TableRow>
              ));
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UnifiedPlanBlock;
